import { Bold, Heading2, Italic, Link2, List } from "lucide-react";
import { useEffect, useRef } from "react";

import { MERGE_FIELDS } from "@/lib/mergeFields";

const SELECT_CLASS =
  "h-8 rounded border border-input bg-card px-2 text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function tokensToPills(html: string): string {
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => {
    const field = MERGE_FIELDS.find((f) => f.token === token);
    return `<span class="merge-pill" data-token="${token}" contenteditable="false">${
      field ? field.label : token
    }</span>`;
  });
}

function pillsToTokens(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(".merge-pill").forEach((el) => {
    const token = el.getAttribute("data-token") ?? "";
    el.replaceWith(document.createTextNode(`{{ ${token} }}`));
  });
  return clone.innerHTML;
}

/** A small dependency-free WYSIWYG editor (contentEditable + toolbar) that emits HTML
 *  with `{{ merge }}` tokens. Merge fields render as atomic pills. Uses execCommand —
 *  deprecated but universally supported for these basic commands. */
export function RichTextEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Sync external changes (template applied, edit loaded) without clobbering the caret
  // on every keystroke — compare in token-space since the DOM holds pills.
  useEffect(() => {
    if (ref.current && pillsToTokens(ref.current) !== value) {
      ref.current.innerHTML = tokensToPills(value || "");
    }
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(pillsToTokens(ref.current));
  };

  const cmd = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL");
    if (url) cmd("createLink", url);
  };

  const insertField = (token: string) => {
    const field = MERGE_FIELDS.find((f) => f.token === token);
    if (!field) return;
    ref.current?.focus();
    document.execCommand(
      "insertHTML",
      false,
      `<span class="merge-pill" data-token="${token}" contenteditable="false">${field.label}</span>&nbsp;`,
    );
    emit();
  };

  const ToolbarButton = ({
    onClick,
    label,
    children,
  }: {
    onClick: () => void;
    label: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep selection
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid size-8 place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-md border border-input bg-card focus-within:ring-2 focus-within:ring-ring">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border/60 px-1.5 py-1">
        <ToolbarButton onClick={() => cmd("bold")} label="Bold">
          <Bold className="size-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => cmd("italic")} label="Italic">
          <Italic className="size-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => cmd("formatBlock", "H2")} label="Heading">
          <Heading2 className="size-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => cmd("insertUnorderedList")} label="Bullet list">
          <List className="size-4" />
        </ToolbarButton>
        <ToolbarButton onClick={addLink} label="Insert link">
          <Link2 className="size-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-border" />
        <select
          aria-label="Insert merge field"
          className={SELECT_CLASS}
          value=""
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            insertField(e.target.value);
            e.currentTarget.value = "";
          }}
        >
          <option value="" disabled>
            + Merge field
          </option>
          {MERGE_FIELDS.map((f) => (
            <option key={f.token} value={f.token}>
              {f.label}
            </option>
          ))}
        </select>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        role="textbox"
        aria-multiline="true"
        className="min-h-40 w-full overflow-y-auto p-3 text-sm leading-relaxed outline-none [&_.merge-pill]:mx-0.5 [&_.merge-pill]:rounded [&_.merge-pill]:bg-primary/10 [&_.merge-pill]:px-1.5 [&_.merge-pill]:py-0.5 [&_.merge-pill]:text-xs [&_.merge-pill]:font-medium [&_.merge-pill]:text-primary [&_a]:text-primary [&_a]:underline [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
      />
    </div>
  );
}
